import CheckCircleIcon from '@material-design-icons/svg/filled/check_circle.svg?react';
import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import TokenIcon from '@material-design-icons/svg/filled/token.svg?react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import type { ReactNode } from 'react';
import { getSolanaExplorerUrl } from '../utils/solanaUtils';
import { Button } from './Button';
import './mint-modal-wallet-button.css';

interface NftMintModalFrameProps {
  title: string;
  description: ReactNode;
  isLoading: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function NftMintModalFrame({
  title,
  description,
  isLoading,
  onClose,
  children,
}: NftMintModalFrameProps) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-neutral-900 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-neutral-700">
        <div className="px-6 py-5 border-b border-neutral-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TokenIcon className="fill-white size-5" />
              <h3 className="text-lg font-semibold text-white">{title}</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-neutral-400 hover:text-white transition-colors"
              disabled={isLoading}
            >
              <CloseIcon className="size-5" />
            </button>
          </div>
          <p className="mt-2 text-sm text-neutral-400">{description}</p>
        </div>

        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

interface NftMintSuccessBlockProps {
  title: string;
  description: ReactNode;
  mintAddress: string;
  txSignature: string;
}

export function NftMintSuccessBlock({
  title,
  description,
  mintAddress,
  txSignature,
}: NftMintSuccessBlockProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 bg-green-900/20 rounded-xl border border-green-700/30">
        <CheckCircleIcon className="size-5 text-green-500 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-green-500">{title}</p>
          <p className="text-xs text-green-400 mt-1">{description}</p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="p-3 bg-neutral-800 rounded-lg">
          <div className="text-xs text-neutral-400 mb-1">Mint Address</div>
          <div className="text-xs text-white font-mono break-all">
            {mintAddress}
          </div>
        </div>

        <div className="p-3 bg-neutral-800 rounded-lg">
          <div className="text-xs text-neutral-400 mb-1">
            Transaction Signature
          </div>
          <div className="text-xs text-white font-mono break-all">
            {txSignature}
          </div>
        </div>
      </div>

      <Button
        variant="secondary"
        onClick={() =>
          window.open(getSolanaExplorerUrl('tx', txSignature), '_blank')
        }
        className="w-full text-sm"
      >
        View Transaction
      </Button>
    </div>
  );
}

interface NftMintRequiredWalletBlockProps {
  walletAddress?: string;
}

export function NftMintRequiredWalletBlock({
  walletAddress,
}: NftMintRequiredWalletBlockProps) {
  if (!walletAddress) {
    return (
      <div className="p-3 bg-red-900/20 rounded-lg border border-red-700/30">
        <p className="text-xs text-red-400">
          No app wallet found. Please ensure your app has a wallet address
          configured.
        </p>
      </div>
    );
  }

  return (
    <div className="p-3 bg-neutral-800 rounded-lg border border-neutral-700">
      <div className="text-xs text-neutral-400 mb-2">Required App Wallet</div>
      <div className="text-xs text-white font-mono">{walletAddress}</div>
    </div>
  );
}

interface NftMintConnectedWalletBlockProps {
  walletAddress: string;
  isCorrectWallet: boolean;
  wrongWalletMessage: string;
  walletButton?: ReactNode;
}

export function NftMintConnectedWalletBlock({
  walletAddress,
  isCorrectWallet,
  wrongWalletMessage,
  walletButton,
}: NftMintConnectedWalletBlockProps) {
  return (
    <div
      className={`p-3 rounded-lg border ${
        isCorrectWallet
          ? 'bg-green-900/20 border-green-700/30'
          : 'bg-red-900/20 border-red-700/30'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs text-neutral-400">
          {isCorrectWallet ? 'Connected Wallet ✓' : 'Wrong Wallet Connected'}
        </div>
        <div className="mint-modal-wallet-button-small">
          {walletButton ?? <WalletMultiButton />}
        </div>
      </div>
      <div className="text-xs text-white font-mono mt-1">{walletAddress}</div>
      {!isCorrectWallet && (
        <p className="text-xs text-red-400 mt-2">{wrongWalletMessage}</p>
      )}
    </div>
  );
}

interface NftMintDisconnectedWalletPromptProps {
  message: string;
  walletButton?: ReactNode;
}

export function NftMintDisconnectedWalletPrompt({
  message,
  walletButton,
}: NftMintDisconnectedWalletPromptProps) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-red-400 text-center">{message}</p>
      <div className="mint-modal-wallet-button">
        {walletButton ?? <WalletMultiButton />}
      </div>
    </div>
  );
}

export function NftMintWrongWalletMessage() {
  return (
    <div className="text-xs text-red-400 text-center">
      Wrong wallet connected. Please switch to the app wallet.
    </div>
  );
}

interface NftMintErrorBlockProps {
  error: string;
  retryCount: number;
}

export function NftMintErrorBlock({
  error,
  retryCount,
}: NftMintErrorBlockProps) {
  return (
    <div className="p-3 bg-red-900/20 rounded-lg border border-red-700/30">
      <p className="text-xs text-red-400">{error}</p>
      {retryCount > 0 && (
        <p className="text-xs text-red-300 mt-1">
          Attempt {retryCount} failed. Please try again.
        </p>
      )}
    </div>
  );
}
